import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, LayoutGrid, List, AlertTriangle, CheckCircle2, Circle, Plus, ShieldCheck, ShieldPlus, FileText, Landmark, CircleDollarSign, StickyNote, Paperclip, ReceiptText, Trash2, Archive, ArchiveRestore, FolderSearch } from 'lucide-react';
import api, { API } from '../lib/api';
import { formatCurrency, formatDate } from '../mock';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { useDeepLink } from '../hooks/useDeepLink';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ServiceInspectionPanel, DeliveryPanel } from '../components/ServiceInspection';
import { InspectionReport } from '../components/InspectionReport';
import { DealHistory } from '../components/DealHistory';

const inputCls = 'w-full bg-muted/40 border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50';

export const DEAL_STAGES = {
  CONTRACT_SIGNED: { label: 'Contract Signed', cls: 'text-sky-400 bg-sky-400/10' },
  WAITING_WARRANTY: { label: 'Warranty Extended', cls: 'text-fuchsia-400 bg-fuchsia-400/10' },
  WAITING_INSURANCE: { label: 'Waiting Insurance', cls: 'text-amber-400 bg-amber-400/10' },
  WAITING_TITLE: { label: 'Waiting Title', cls: 'text-orange-400 bg-orange-400/10' },
  WAITING_REGISTRATION: { label: 'Waiting Registration', cls: 'text-violet-400 bg-violet-400/10' },
  REGISTRATION_COMPLETE: { label: 'Registration Completed', cls: 'text-teal-400 bg-teal-400/10' },
  FUNDING: { label: 'Funding', cls: 'text-yellow-400 bg-yellow-400/10' },
  SERVICE: { label: 'Service', cls: 'text-blue-400 bg-blue-400/10' },
  READY_FOR_DELIVERY: { label: 'Ready for Delivery', cls: 'text-emerald-400 bg-emerald-400/10' },
  DELIVERED: { label: 'Delivered', cls: 'text-muted-foreground bg-muted/40' },
};

const subBadge = (status) => {
  const ok = ['Verified', 'Received', 'In House', 'Completed', 'Funded', 'Approved', 'Not Required', 'Not Applicable', 'Ready for Registration', 'Sent to Registration'];
  const warn = ['Missing', 'Not In House', 'Needs Request', 'Pending', 'Stipulation Required'];
  if (ok.includes(status)) return <span className="text-[11px] font-semibold text-emerald-400">✓ {status}</span>;
  if (warn.includes(status)) return <span className="text-[11px] font-semibold text-amber-400">⚠ {status}</span>;
  return <span className="text-[11px] font-semibold text-muted-foreground">⏳ {status}</span>;
};

const warBadge = (w) => {
  if (!w || !w.sold) return <span className="text-[11px] font-semibold text-amber-400">⚠ Pendente</span>;
  if (w.sold === 'no') return <span className="text-[11px] font-semibold text-muted-foreground">— Não vendida</span>;
  if (w.issued && w.paid) return <span className="text-[11px] font-semibold text-emerald-400">✓ Emitida e Paga</span>;
  return <span className="text-[11px] font-semibold text-amber-400">⚠ {!w.issued ? 'Aguardando emissão' : 'Aguardando pagamento'}</span>;
};

const fmtDT = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
};

const AgeBadge = ({ days }) => (
  <span className={`num text-[10.5px] font-semibold ${days >= 5 ? 't-bad' : days >= 3 ? 't-warn' : 't-label'}`}>{days}d</span>
);

const OK_STATES = ['Verified', 'Received', 'In House', 'Completed', 'Funded', 'Approved', 'Not Required', 'Not Applicable', 'Ready for Registration', 'Sent to Registration'];
const BAD_STATES = ['Missing', 'Not In House', 'Needs Request'];
const WARN_STATES = ['Pending', 'Stipulation Required', 'Requested from Floor', 'Submitted', 'Not In House', 'Needs Request'];

export const STAGE_BAR = {
  CONTRACT_SIGNED: '#38bdf8', WAITING_WARRANTY: '#a855f7', WAITING_INSURANCE: '#eab308',
  WAITING_TITLE: '#f97316', WAITING_REGISTRATION: '#8b5cf6', REGISTRATION_COMPLETE: '#14b8a6',
  FUNDING: '#facc15', SERVICE: '#3b82f6', READY_FOR_DELIVERY: '#10b981', DELIVERED: '#6b7280',
};

const FiIcon = ({ state }) => {
  if (OK_STATES.includes(state)) return <CheckCircle2 className="w-3.5 h-3.5 text-[hsl(var(--ok))]" />;
  if (WARN_STATES.includes(state)) return <AlertTriangle className="w-3.5 h-3.5 text-[hsl(var(--warn))]" />;
  return <Circle className="w-3.5 h-3.5 text-muted-foreground/40" />;
};

const ManualDealDialog = ({ open, onClose, onCreated }) => {
  const [vehicles, setVehicles] = useState([]);
  const [team, setTeam] = useState([]);
  const [f, setF] = useState({ cliente: '', telefone: '', email: '', vehicle_id: '', salesperson_name: '', finance_type: 'Cash', sale_price: '', sale_date: new Date().toISOString().slice(0, 10), stage: 'CONTRACT_SIGNED', reason: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF((p) => ({ ...p, sale_date: new Date().toISOString().slice(0, 10) }));
    api.get('/vehicles').then(({ data }) => setVehicles(data.filter((v) => !['Vendido', 'Entregue'].includes(v.status)))).catch(() => {});
    api.get('/users').then(({ data }) => setTeam(data)).catch(() => {});
  }, [open]);

  const veh = vehicles.find((v) => v.id === f.vehicle_id);

  const save = async () => {
    if (!f.cliente.trim()) { toast({ title: 'Informe o nome do cliente' }); return; }
    if (!f.vehicle_id) { toast({ title: 'Selecione o veículo' }); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/deals/manual', { ...f, sale_price: Number(f.sale_price) || veh?.preco || 0 });
      toast({ title: `Deal manual ${data.id} criado`, description: 'Veículo marcado como vendido — este deal não conta como venda.' });
      onCreated && onCreated();
      onClose();
      setF({ cliente: '', telefone: '', email: '', vehicle_id: '', salesperson_name: '', finance_type: 'Cash', sale_price: '', sale_date: new Date().toISOString().slice(0, 10), stage: 'CONTRACT_SIGNED', reason: '', notes: '' });
    } catch (e) { toast({ title: 'Erro ao criar', description: e.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display font-bold text-lg uppercase tracking-tight">Adicionar deal manualmente</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-[11.5px] t-warn border-l-2 border-[hsl(var(--warn))] pl-2.5">Este deal <b>não será contado como venda</b> (fica fora de Vendas no mês, Faturamento, Vendas recentes, metas e Relatório de Profit). O veículo será marcado como Vendido.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1"><label className="ent-label block mb-1">Cliente *</label><input data-testid="md-cliente" className={inputCls} value={f.cliente} onChange={(e) => setF({ ...f, cliente: e.target.value })} /></div>
            <div><label className="ent-label block mb-1">Telefone</label><input data-testid="md-telefone" className={inputCls} value={f.telefone} onChange={(e) => setF({ ...f, telefone: e.target.value })} /></div>
            <div><label className="ent-label block mb-1">E-mail</label><input className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
            <div className="sm:col-span-2">
              <label className="ent-label block mb-1">Veículo *</label>
              <select data-testid="md-vehicle" className={inputCls} value={f.vehicle_id} onChange={(e) => { const v = vehicles.find((x) => x.id === e.target.value); setF({ ...f, vehicle_id: e.target.value, sale_price: v?.preco || '' }); }}>
                <option value="" className="bg-popover">Selecione o veículo</option>
                {vehicles.map((v) => <option key={v.id} value={v.id} className="bg-popover">{v.ano} {v.marca} {v.modelo} · #{v.placa} · {formatCurrency(v.preco)}</option>)}
              </select>
            </div>
            <div><label className="ent-label block mb-1">Preço de venda ($)</label><input data-testid="md-price" type="number" step="0.01" className={inputCls} value={f.sale_price} onChange={(e) => setF({ ...f, sale_price: e.target.value })} /></div>
            <div>
              <label className="ent-label block mb-1">Vendedor</label>
              <select data-testid="md-salesperson" className={inputCls} value={f.salesperson_name} onChange={(e) => setF({ ...f, salesperson_name: e.target.value })}>
                <option value="" className="bg-popover">— Selecione</option>
                {team.map((t) => <option key={t.user_id} value={t.name} className="bg-popover">{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="ent-label block mb-1">Tipo</label>
              <select data-testid="md-finance-type" className={inputCls} value={f.finance_type} onChange={(e) => setF({ ...f, finance_type: e.target.value })}>
                {['Cash', 'Finance', 'Outside Finance', 'Lease'].map((t) => <option key={t} className="bg-popover">{t}</option>)}
              </select>
            </div>
            <div><label className="ent-label block mb-1">Data do contrato</label><input data-testid="md-date" type="date" className={inputCls} value={f.sale_date} onChange={(e) => setF({ ...f, sale_date: e.target.value })} /></div>
            <div className="sm:col-span-2">
              <label className="ent-label block mb-1">Etapa inicial do fluxo</label>
              <select data-testid="md-stage" className={inputCls} value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })}>
                {Object.entries(DEAL_STAGES).map(([k, m]) => <option key={k} value={k} className="bg-popover">{m.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-3"><label className="ent-label block mb-1">Motivo do lançamento manual</label><input data-testid="md-reason" className={inputCls} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Ex: venda de funcionário, transferência entre lojas..." /></div>
            <div className="sm:col-span-3"><label className="ent-label block mb-1">Observações</label><input className={inputCls} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="ent-btn">Cancelar</button>
          <button data-testid="md-save" onClick={save} disabled={saving} className="ent-btn ent-btn-primary">{saving ? 'Criando...' : 'Criar deal manual'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DealRow = ({ d, onOpen, canOpen = true }) => {
  const w = d.warranty || {};
  const warState = w.sold === 'no' || (w.sold === 'yes' && w.issued && w.paid) ? 'Completed' : w.sold ? 'Pending' : 'Missing';
  const micro = [['INS', d.insurance?.status], ['WAR', warState], ['TTL', d.title?.status], ['REG', d.registration?.status]];
  return (
    <tr data-testid={`deal-card-${d.id}`} onClick={() => canOpen && onOpen(d.id)} className={canOpen ? '' : 'cursor-default'}>
      <td>
        <p className="text-[11.5px] font-semibold uppercase tracking-wide leading-tight text-foreground truncate">{d.customer_name}</p>
        <p className="t-label mt-0.5 leading-tight truncate">{d.vehicle_desc}</p>
        <p className="mono t-label text-[10.5px] truncate">#{d.stock} · {d.id}</p>
        <p className="num text-[11.5px] font-semibold mt-0.5" data-testid={`deal-price-${d.id}`}>{formatCurrency(d.sale_price || 0)}</p>
        {(d.booked || d.funded_flag || d.manual || d.canceled) && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {d.canceled && <span data-testid={`badge-canceled-${d.id}`} className="ent-tag t-bad" title={`Venda cancelada por ${d.canceled_by || ''}`}>CANCELADA</span>}
            {d.manual && <span data-testid={`badge-manual-${d.id}`} className="ent-tag t-warn" title="Deal lançado manualmente — não conta como venda">Manual</span>}
            {d.booked && <span data-testid={`badge-booked-${d.id}`} className="ent-tag">Booked</span>}
            {d.funded_flag && <span data-testid={`badge-funding-${d.id}`} className="ent-tag t-ok">Funding</span>}
          </div>
        )}
      </td>
      <td className="text-center align-middle"><AgeBadge days={d.deal_age} /></td>
      <td className="text-primary leading-tight">{d.next_action}</td>
      <td className="align-middle">
        <div className="flex items-center justify-center gap-1.5">
          {micro.map(([label, state]) => <span key={label} title={`${label}: ${state || '—'}`}><FiIcon state={state} /></span>)}
        </div>
      </td>
    </tr>
  );
};

const Field = ({ label, children }) => (
  <div><label className="text-xs text-muted-foreground mb-1.5 block">{label}</label>{children}</div>
);

const BackendBlock = ({ vehicleId, dealId }) => {
  const { user } = useAuth();
  const isFinView = ['Administrator', 'Owner'].includes(user?.role) || (user?.role || '').endsWith('Manager');
  const [be, setBe] = useState(null);
  const [form, setForm] = useState({});
  const [fin, setFin] = useState({ down_payment: '', net_check: '', lender: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!dealId || !isFinView) return;
    try {
      const { data } = await api.get(`/deals/${dealId}/finance`);
      setBe(data);
      setForm(Object.fromEntries(Object.entries(data.products).map(([k, p]) => [k, { cost: p.cost || '', markup: p.markup || '' }])));
      setFin({ down_payment: data.finance.down_payment || '', net_check: data.finance.net_check || '', lender: data.finance.lender || '' });
    } catch (e) {}
  }, [dealId, isFinView]);
  useEffect(() => { load(); }, [load]);

  if (!isFinView || !be) return null;

  const r = be.result || {};

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, p]) => [k, { cost: Number(p.cost) || 0, markup: Number(p.markup) || 0 }]));
      if (vehicleId) await api.put(`/vehicles/${vehicleId}/backend`, payload);
      await api.put(`/deals/${dealId}/finance`, { down_payment: Number(fin.down_payment) || 0, net_check: Number(fin.net_check) || 0, lender: fin.lender || '' });
      toast({ title: 'Financeiro atualizado' });
      await load();
    } catch (e) { toast({ title: 'Erro ao salvar', description: e.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };

  return (
    <div className="col-span-2 rounded-lg border border-border/70 p-3.5 space-y-3" data-testid="deal-backend-block">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] text-muted-foreground/70 uppercase font-bold flex items-center gap-1"><CircleDollarSign className="w-3 h-3" /> Financeiro do deal</p>
        <p className="text-[11px] text-muted-foreground">Back-end líquido: <b className="text-fuchsia-400" data-testid="deal-be-total-net">{formatCurrency(be.totals?.net || 0)}</b></p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div><label className="text-[10px] text-muted-foreground mb-1 block">Entrada ($)</label><input data-testid="deal-fin-down" type="number" step="0.01" disabled={!be.can_manage} className={inputCls} value={fin.down_payment} onChange={(e) => setFin({ ...fin, down_payment: e.target.value })} /></div>
        <div><label className="text-[10px] text-muted-foreground mb-1 block">Net Check ($)</label><input data-testid="deal-fin-netcheck" type="number" step="0.01" disabled={!be.can_manage} className={inputCls} value={fin.net_check} onChange={(e) => setFin({ ...fin, net_check: e.target.value })} /></div>
        <div><label className="text-[10px] text-muted-foreground mb-1 block">Banco / Lender</label><input data-testid="deal-fin-lender" disabled={!be.can_manage} className={inputCls} value={fin.lender} onChange={(e) => setFin({ ...fin, lender: e.target.value })} placeholder="Ex: Westlake" /></div>
      </div>
      <div className="space-y-2">
        <p className="text-[10px] text-muted-foreground/70 uppercase font-bold">Back-End comercializado</p>
        {Object.entries(be.products || {}).map(([k, p]) => {
          const f = form[k] || { cost: '', markup: '' };
          const net = (Number(f.markup) || 0) - (Number(f.cost) || 0);
          return (
            <div key={k} className="grid grid-cols-[1fr_110px_110px_90px] gap-2 items-end rounded-lg border border-border/70 bg-popover px-3.5 py-2.5">
              <p className="text-[13px] font-semibold">{p.label}</p>
              <div><label className="text-[10px] text-muted-foreground mb-1 block">Custo ($)</label><input data-testid={`deal-be-${k}-cost`} type="number" step="0.01" disabled={!be.can_manage} className={inputCls} value={f.cost} onChange={(e) => setForm({ ...form, [k]: { ...f, cost: e.target.value } })} /></div>
              <div><label className="text-[10px] text-muted-foreground mb-1 block">Markup ($)</label><input data-testid={`deal-be-${k}-markup`} type="number" step="0.01" disabled={!be.can_manage} className={inputCls} value={f.markup} onChange={(e) => setForm({ ...form, [k]: { ...f, markup: e.target.value } })} /></div>
              <div className="text-right"><p className="text-[10px] text-muted-foreground">Lucro</p><p className={`text-[13px] font-bold tabular-nums ${net >= 0 ? 't-ok' : 't-bad'}`} data-testid={`deal-be-${k}-net`}>{formatCurrency(net)}</p></div>
            </div>
          );
        })}
      </div>
      <div className="rounded-lg border border-border/70 bg-muted/15 p-3.5" data-testid="deal-result-block">
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground/70">Resultado do deal</p>
          <p className="text-[10px] t-label">{be.finance_type || '—'} · Receita = Net Check + Entrada</p>
        </div>
        {r.cost_missing && (
          <p data-testid="deal-cost-missing" className="text-[12px] font-semibold t-bad border-l-2 border-[hsl(var(--bad))] pl-2.5 mb-2.5">
            ⚠ Custo do veículo não cadastrado — o resultado abaixo está <b>incompleto</b>.
            {be.vehicle?.stock ? ` Lance o custo no card do veículo #${be.vehicle.stock} (aba Valores).` : ''}
          </p>
        )}
        {r.revenue_missing && (
          <p data-testid="deal-revenue-missing" className="text-[12px] font-semibold t-warn border-l-2 border-[hsl(var(--warn))] pl-2.5 mb-2.5">
            ⚠ Sem receita lançada — preencha o Net Check (financiado) ou a Entrada (cash) acima.
          </p>
        )}
        <div className="grid grid-cols-[1fr_auto] gap-y-1.5 text-[12px]">
          <span className="text-muted-foreground">Net Check (receita principal)</span>
          <span className="num font-semibold text-right" data-testid="dr-netcheck">{formatCurrency(r.net_check || 0)}</span>
          <span className="text-muted-foreground">+ Entrada</span>
          <span className="num font-semibold text-right" data-testid="dr-down">{formatCurrency(r.down_payment || 0)}</span>
          <span className="text-muted-foreground">+ Markups back-end</span>
          <span className="num font-semibold text-right text-fuchsia-400" data-testid="dr-markup">{formatCurrency(r.markup || 0)}</span>
          <span className="text-muted-foreground">− Custo dos produtos (GAP / Warranty / Tire)</span>
          <span className="num text-right" data-testid="dr-prodcost">− {formatCurrency(r.product_cost || 0)}</span>
          <span className={`${r.cost_missing ? 't-bad font-semibold' : 'text-muted-foreground'}`}>− Custo do veículo {r.cost_missing && '(faltando)'}</span>
          <span className={`num text-right ${r.cost_missing ? 't-bad' : ''}`} data-testid="dr-vehcost">{r.cost_missing ? 'NÃO CADASTRADO' : `− ${formatCurrency(r.vehicle_cost || 0)}`}</span>
          <span className="text-muted-foreground">− Gastos do veículo ({(be.vehicle?.expenses || []).length})</span>
          <span className="num text-right text-orange-400" data-testid="dr-expenses">− {formatCurrency(r.expenses_total || 0)}</span>
        </div>
        {!!(be.vehicle?.expenses || []).length && (
          <div className="mt-2.5 border-t border-border/60 pt-2 space-y-1" data-testid="deal-expenses-list">
            {be.vehicle.expenses.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-2 text-[11px]">
                <span className="truncate text-foreground/75">{e.category ? `${e.category} · ` : ''}{e.label}<span className="t-label"> {(e.date || '').slice(0, 10)}</span></span>
                <span className="num text-orange-400">{formatCurrency(e.amount)}</span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-border/70 flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] t-label uppercase">{r.cost_missing || r.revenue_missing ? 'Resultado provisório' : 'Resultado do deal'}</p>
            <p className="font-display font-black text-xl num" data-testid="deal-be-profit">
              <span className={(r.profit || 0) >= 0 ? 't-ok' : 't-bad'}>{formatCurrency(r.profit || 0)}</span>
              {(r.cost_missing || r.revenue_missing) && <span className="ent-tag t-warn ml-2">INCOMPLETO</span>}
            </p>
            <p className="text-[10px] t-label mt-0.5">Preço de tabela do veículo: {formatCurrency(be.vehicle?.preco || 0)} (informativo)</p>
          </div>
          {be.can_manage && <button data-testid="deal-be-save" onClick={save} disabled={saving} className="ent-btn ent-btn-primary">{saving ? 'Salvando...' : 'Salvar financeiro'}</button>}
        </div>
      </div>
    </div>
  );
};

const FlagToggle = ({ label, active, onToggle, disabled, testid }) => (
  <button data-testid={testid} disabled={disabled} onClick={onToggle}
    className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] font-black tracking-wide transition-colors disabled:opacity-40 ${active ? (label === 'BOOKED' ? 'border-indigo-400/50 text-indigo-300 bg-indigo-500/12' : 'border-emerald-400/50 text-emerald-400 bg-emerald-500/12') : 'border-border text-muted-foreground hover:text-foreground'}`}>
    <span className={`w-[34px] h-[18px] rounded-full p-0.5 flex transition-colors ${active ? (label === 'BOOKED' ? 'bg-indigo-500 justify-end' : 'bg-emerald-500 justify-end') : 'bg-muted justify-start'}`}>
      <span className="w-3.5 h-3.5 rounded-full bg-white transition-all" />
    </span>
    {label}
  </button>
);

export const DealDetailDialog = ({ dealId, open, onClose, onChanged }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [form, setForm] = useState({});
  const [note, setNote] = useState('');
  const [delOpen, setDelOpen] = useState(false);
  const [delReason, setDelReason] = useState('');
  const [delConfirm, setDelConfirm] = useState('');
  const [delReturn, setDelReturn] = useState(true);
  const [delBusy, setDelBusy] = useState(false);
  const [swapOpen, setSwapOpen] = useState(false);
  const [sendingFunding, setSendingFunding] = useState(false);
  const [swapQ, setSwapQ] = useState('');
  const [swapList, setSwapList] = useState([]);
  const docFileRef = useRef(null);
  const docKeyRef = useRef(null);
  const canEdit = ['Owner', 'Administrator', 'Finance Manager'].includes(user?.role);
  const canInspect = ['Owner', 'Administrator', 'General Manager', 'Service Manager', 'Service Advisor', 'Service Inspector'].includes(user?.role);
  const canDeliver = ['Owner', 'Administrator', 'General Manager', 'Sales Manager'].includes(user?.role);

  const load = useCallback(async () => {
    try { const { data: r } = await api.get(`/deals/${dealId}`); setData(r); } catch (e) {}
  }, [dealId]);
  useEffect(() => { if (open && dealId) { setData(null); setTab('overview'); setForm({}); load(); } }, [open, dealId, load]);

  const d = data?.deal;
  const save = async (path, body, okMsg) => {
    try {
      await api.put(`/deals/${dealId}/${path}`, body);
      toast({ title: okMsg || 'Atualizado' });
      setForm({}); load(); onChanged && onChanged();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const setDoc = (key, status) => {
    const docs = d.documents.map((x) => x.key === key ? { ...x, status } : x);
    save('documents', { documents: docs }, 'Checklist atualizado');
  };

  const toggleFlag = async (key) => {
    try {
      await api.put(`/deals/${dealId}/flags`, { [key]: !d[key] });
      toast({ title: `${key === 'booked' ? 'Booked' : 'Funding'} ${!d[key] ? 'ativado' : 'desativado'}` });
      load(); onChanged && onChanged();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const openSwap = async () => {
    setSwapOpen((o) => !o); setSwapQ('');
    try { const r = await api.get('/vehicles'); setSwapList(r.data.filter((v) => v.status !== 'Vendido' && v.status !== 'Entregue')); } catch (e) {}
  };
  const doSwap = async (v) => {
    if (!window.confirm(`Trocar o veículo do deal para ${v.ano} ${v.marca} ${v.modelo} (#${v.placa})?\nO veículo atual (${d.vehicle_desc}) voltará ao estoque como Disponível.`)) return;
    try {
      await api.post(`/deals/${dealId}/swap-vehicle`, { vehicle_id: v.id });
      toast({ title: 'Veículo trocado', description: `${d.vehicle_desc} voltou ao estoque como Disponível.` });
      setSwapOpen(false); load(); onChanged && onChanged();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const warFileRef = useRef(null);
  const uploadWarrantyFile = async (files) => {
    if (!files?.length) return;
    try {
      const fd = new FormData();
      fd.append('file', files[0]);
      await api.post(`/deals/${dealId}/warranty/file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: 'Anexo da garantia salvo' });
      load(); onChanged && onChanged();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };
  const delWarrantyFile = async (fid) => {
    try { await api.delete(`/deals/${dealId}/warranty/file/${fid}`); load(); onChanged && onChanged(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const attachDoc = (key) => { docKeyRef.current = key; docFileRef.current?.click(); };
  const uploadDocFile = async (files) => {
    const key = docKeyRef.current;
    if (!files?.length || !key) return;
    try {
      const fd = new FormData();
      fd.append('file', files[0]);
      await api.post(`/deals/${dealId}/documents/${key}/file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: 'Documento anexado' });
      load(); onChanged && onChanged();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };
  const delDocFile = async (key, fid) => {
    try { await api.delete(`/deals/${dealId}/documents/${key}/file/${fid}`); load(); onChanged && onChanged(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const requestFloor = async () => {
    try {
      await api.post(`/deals/${dealId}/title/request-floor`, { floor_company: form.floor_company || '', expected_date: form.expected_date || '', reference: form.reference || '', notes: form.title_notes || '' });
      toast({ title: 'Title requested from Floor' }); setForm({}); load(); onChanged && onChanged();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/deals/${dealId}/notes`, { note, module: tab }); setNote(''); load(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const archive = async () => {
    try { await api.post(`/deals/${dealId}/archive`); toast({ title: 'Deal arquivado' }); load(); onChanged && onChanged(); onClose(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };
  const unarchive = async () => {
    try { await api.post(`/deals/${dealId}/unarchive`); toast({ title: 'Deal desarquivado' }); load(); onChanged && onChanged(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };
  const doDelete = async () => {
    setDelBusy(true);
    try {
      const { data } = await api.post(`/deals/${dealId}/delete`, { reason: delReason, confirm_deal_id: delConfirm, return_vehicle: delReturn });
      toast({ title: 'Venda excluída', description: `${dealId} removido${data.vehicle_returned ? ' · veículo voltou ao estoque' : ''}` });
      setDelOpen(false); onChanged && onChanged(); onClose();
    } catch (e) { toast({ title: 'Erro ao excluir', description: e.response?.data?.detail || '' }); }
    finally { setDelBusy(false); }
  };

  const tabs = ['overview', 'timeline', 'documents', 'warranty', 'insurance', 'title', 'registration', 'service', 'notes'];
  const stage = d && (DEAL_STAGES[d.status] || { label: d.status, cls: '' });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-3xl max-h-[90vh] overflow-y-auto">
        {!d ? (
          <><DialogHeader><DialogTitle className="sr-only">Carregando</DialogTitle></DialogHeader><p className="text-muted-foreground text-sm py-8 text-center">Carregando...</p></>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display font-black text-2xl uppercase flex flex-wrap items-center gap-3" data-testid="deal-detail-title">
                {d.customer_name} <span className={`text-[11px] px-2.5 py-1 rounded-full font-semibold tracking-wide ${stage.cls}`}>{stage.label}</span>
                {d.archived && <span className="text-[10px] px-2 py-1 border border-border text-muted-foreground font-semibold tracking-wide" style={{ borderRadius: 3 }}>ARQUIVADO</span>}
              </DialogTitle>
            </DialogHeader>
            {canEdit && (
              <div className="flex flex-wrap items-center gap-2 -mt-1">
                {d.status === 'DELIVERED' && !d.archived && (
                  <button data-testid="deal-archive-btn" onClick={archive} className="ent-btn h-8 px-3"><Archive className="w-3.5 h-3.5" /> Arquivar deal</button>
                )}
                {d.archived && (
                  <button data-testid="deal-unarchive-btn" onClick={unarchive} className="ent-btn h-8 px-3"><ArchiveRestore className="w-3.5 h-3.5" /> Desarquivar</button>
                )}
                <button data-testid="deal-delete-btn" onClick={() => { setDelOpen(true); setDelReason(''); setDelConfirm(''); setDelReturn(true); }}
                  className="ent-btn h-8 px-3 text-[11.5px]" style={{ borderColor: 'hsl(var(--bad) / 0.5)', color: 'hsl(var(--bad))' }}>
                  <Trash2 className="w-3.5 h-3.5" /> Excluir venda
                </button>
                {d.archived_at && <span className="text-[11px] text-muted-foreground">arquivado em {new Date(d.archived_at).toLocaleDateString('pt-BR')} por {d.archived_by}</span>}
              </div>
            )}
            {delOpen && (
              <div className="border p-3.5 space-y-3" style={{ borderColor: 'hsl(var(--bad) / 0.45)', background: 'hsl(var(--bad) / 0.05)', borderRadius: 3 }} data-testid="deal-delete-panel">
                <div>
                  <p className="text-[12.5px] font-bold" style={{ color: 'hsl(var(--bad))' }}>Excluir esta venda permanentemente</p>
                  <p className="text-[11.5px] text-muted-foreground mt-0.5">O deal sai do F&I, do histórico de vendas, das comissões e dos relatórios. Fica registrado no log de auditoria (quem excluiu, quando e o motivo).</p>
                </div>
                <div>
                  <p className="ent-label mb-1">O que fazer com o veículo?</p>
                  <div className="flex flex-col gap-1.5">
                    <label className="flex items-center gap-2 text-[12.5px] cursor-pointer">
                      <input data-testid="del-vehicle-return" type="radio" checked={delReturn} onChange={() => setDelReturn(true)} /> Voltar para o estoque como <b>Disponível</b>
                    </label>
                    <label className="flex items-center gap-2 text-[12.5px] cursor-pointer">
                      <input data-testid="del-vehicle-keep" type="radio" checked={!delReturn} onChange={() => setDelReturn(false)} /> Manter o veículo como <b>Vendido</b>
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><p className="ent-label mb-1">Motivo da exclusão</p>
                    <input data-testid="del-reason" className="ent-input w-full" value={delReason} onChange={(e) => setDelReason(e.target.value)} placeholder="Ex: venda cancelada pelo cliente" /></div>
                  <div><p className="ent-label mb-1">Digite {d.id} para confirmar</p>
                    <input data-testid="del-confirm" className="ent-input w-full num" value={delConfirm} onChange={(e) => setDelConfirm(e.target.value.toUpperCase())} placeholder={d.id} /></div>
                </div>
                <div className="flex gap-2">
                  <button data-testid="del-cancel" onClick={() => setDelOpen(false)} className="ent-btn h-8 px-3">Cancelar</button>
                  <button data-testid="del-confirm-btn" disabled={delBusy || delConfirm !== d.id || delReason.trim().length < 4} onClick={doDelete}
                    className="ent-btn h-8 px-4 font-bold text-white disabled:opacity-40" style={{ background: 'hsl(var(--bad))', borderColor: 'hsl(var(--bad))' }}>
                    Excluir venda definitivamente
                  </button>
                </div>
              </div>
            )}
            <div className="rounded-lg border border-border/70 bg-popover p-3.5 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-[10px] text-muted-foreground/70 uppercase font-bold">Vehicle</p>
                <p className="font-medium">{d.vehicle_desc}</p>
                <p className="text-[11px] text-muted-foreground">#{d.stock} · {d.vin || '—'}</p>
                {canEdit && <button data-testid="swap-vehicle-btn" onClick={openSwap} className="mt-1 text-[11px] font-bold text-primary hover:underline">⇄ Trocar Veículo</button>}
              </div>
              <div><p className="text-[10px] text-muted-foreground/70 uppercase font-bold">Salesperson</p><p>{d.salesperson_name}</p><p className="text-[11px] text-muted-foreground">{d.id} · {d.sale_date}</p></div>
              <div><p className="text-[10px] text-muted-foreground/70 uppercase font-bold">Deal Age</p><AgeBadge days={d.deal_age} /></div>
              <div><p className="text-[10px] text-muted-foreground/70 uppercase font-bold">Next Action</p><p className="text-primary font-bold text-[13px] uppercase" data-testid="deal-next-action">→ {d.next_action}</p></div>
            </div>
            {swapOpen && (
              <div className="rounded-lg border border-primary/35 bg-primary/[0.04] p-3 space-y-2" data-testid="swap-vehicle-panel">
                <input autoFocus className={inputCls} placeholder="Buscar por ano, marca, modelo, VIN ou stock..." value={swapQ} onChange={(e) => setSwapQ(e.target.value)} data-testid="swap-vehicle-search" />
                <div className="max-h-[200px] overflow-y-auto space-y-0.5">
                  {swapList.filter((v) => `${v.ano} ${v.marca} ${v.modelo} ${v.vin || ''} ${v.placa || ''}`.toLowerCase().includes(swapQ.toLowerCase())).slice(0, 30).map((v) => (
                    <button key={v.id} data-testid={`swap-pick-${v.id}`} onClick={() => doSwap(v)} className="w-full text-left text-[12px] px-2.5 py-2 rounded hover:bg-muted/50 transition-colors flex items-center justify-between gap-2">
                      <span>{v.ano} {v.marca} {v.modelo} · #{v.placa}{v.vin ? ` · VIN ${v.vin}` : ''}</span>
                      <span className="font-semibold tabular-nums shrink-0">${Number(v.preco).toLocaleString('en-US')}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <FlagToggle label="BOOKED" active={!!d.booked} disabled={!canEdit} onToggle={() => toggleFlag('booked')} testid="flag-booked" />
              <FlagToggle label="FUNDING" active={!!d.funded_flag} disabled={!canEdit} onToggle={() => toggleFlag('funded_flag')} testid="flag-funding" />
            </div>
            {/* mini roadmap */}
            <div className="flex items-center gap-1 flex-wrap py-1">
              {Object.entries(DEAL_STAGES).map(([k, m], i) => {
                const idx = Object.keys(DEAL_STAGES).indexOf(d.status);
                const done = i < idx; const cur = i === idx;
                return (
                  <React.Fragment key={k}>
                    {i > 0 && <span className={`h-px w-4 shrink-0 ${done || cur ? 'bg-primary' : 'bg-muted/60'}`} />}
                    <span className={`text-[9px] px-1.5 py-1 rounded whitespace-nowrap font-semibold ${cur ? 'bg-primary text-white animate-pulse' : done ? 'text-emerald-400 bg-emerald-400/10' : 'text-muted-foreground/60 bg-muted/40'}`}>{m.label}</span>
                  </React.Fragment>
                );
              })}
            </div>
            {d.status === 'FUNDING' && canEdit && (
              <div className="space-y-1.5">
                <button data-testid="send-to-funding-btn" disabled={sendingFunding} onClick={async () => {
                  setSendingFunding(true);
                  try {
                    await api.post(`/deals/${dealId}/send-to-funding`);
                    toast({ title: 'Enviado para Funding', description: 'Funding criado como Pending e deal avançado para Service' });
                    load(); onChanged && onChanged();
                  } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
                  finally { setSendingFunding(false); }
                }} className="w-full py-3 rounded-lg text-sm font-black uppercase tracking-wide bg-yellow-500 hover:bg-yellow-600 text-black transition-colors disabled:opacity-60">
                  {sendingFunding ? 'Enviando...' : 'Send to Funding & Continue to Service'}
                </button>
                <p className="text-[11px] t-label">Cria o registro em Financeiro › Fundings como <b>Pending</b> e libera o veículo para o Service. O recebimento do banco é confirmado depois, em Financeiro › Fundings.</p>
              </div>
            )}
            {d.status === 'FUNDING' && !canEdit && <p className="text-[12px] text-yellow-400 font-semibold">Aguardando o F&I enviar o contrato ao banco (Send to Funding).</p>}
            <div className="flex gap-1 flex-wrap bg-muted/40 border border-border rounded-lg p-1 w-fit">
              {tabs.map((t) => (
                <button key={t} data-testid={`deal-tab-${t}`} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition-colors ${tab === t ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}>{t}</button>
              ))}
            </div>

            {tab === 'overview' && (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-border/70 p-3.5 space-y-1"><p className="text-[10px] text-muted-foreground/70 uppercase font-bold flex items-center gap-1"><ShieldPlus className="w-3 h-3" /> Warranty Ext.</p>{warBadge(d.warranty)}<p className="text-[11px] text-muted-foreground">{d.warranty?.sold === 'yes' ? `${d.warranty?.company || ''} ${d.warranty?.term_months ? `· ${d.warranty.term_months} meses` : ''}` : ''}</p></div>
                <div className="rounded-lg border border-border/70 p-3.5 space-y-1"><p className="text-[10px] text-muted-foreground/70 uppercase font-bold flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Insurance</p>{subBadge(d.insurance?.status)}<p className="text-[11px] text-muted-foreground">{d.insurance?.company || ''} {d.insurance?.policy_number || ''}</p></div>
                <div className="rounded-lg border border-border/70 p-3.5 space-y-1"><p className="text-[10px] text-muted-foreground/70 uppercase font-bold flex items-center gap-1"><FileText className="w-3 h-3" /> Title</p>{subBadge(d.title?.status)}<p className="text-[11px] text-muted-foreground">{d.title?.floor_company || d.title?.location || ''}</p></div>
                <div className="rounded-lg border border-border/70 p-3.5 space-y-1"><p className="text-[10px] text-muted-foreground/70 uppercase font-bold flex items-center gap-1"><Landmark className="w-3 h-3" /> Registration</p>{subBadge(d.registration?.status)}<p className="text-[11px] text-muted-foreground">{d.registration?.plate_number ? `Plate: ${d.registration.plate_number}` : ''}</p></div>
                <div className="rounded-lg border border-border/70 p-3.5 space-y-1"><p className="text-[10px] text-muted-foreground/70 uppercase font-bold flex items-center gap-1"><CircleDollarSign className="w-3 h-3" /> Payment</p><p className="text-sm font-semibold">{d.finance_type}</p><p className="text-[11px] text-muted-foreground">Financeiro gerido no Dealer Center</p></div>
                <div className="col-span-2 rounded-lg border border-border/70 p-3.5"><p className="text-[10px] text-muted-foreground/70 uppercase font-bold mb-2">Documents</p>
                  <div className="flex flex-wrap gap-2">{(d.documents || []).map((doc) => (
                    <span key={doc.key} className={`text-[11px] px-2 py-1 rounded-full font-medium ${doc.status === 'complete' ? 'text-emerald-400 bg-emerald-400/10' : doc.status === 'not_required' ? 'text-muted-foreground/70 bg-muted/40' : 'text-amber-400 bg-amber-400/10'}`}>{doc.status === 'complete' ? '✓' : doc.status === 'not_required' ? '—' : '⚠'} {doc.label}</span>
                  ))}</div>
                </div>
                <BackendBlock vehicleId={d.vehicle_id} dealId={d.id} />
              </div>
            )}

            {tab === 'timeline' && (
              <div className="space-y-0 max-h-[340px] overflow-y-auto" data-testid="deal-timeline">
                {(data.activities || []).map((a) => (
                  <div key={a.id} className="flex gap-3 relative pb-4 last:pb-0">
                    <div className="flex flex-col items-center"><span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" /><span className="w-px flex-1 bg-muted/50" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{a.action}</p>
                      <p className="text-[11px] text-muted-foreground">{fmtDT(a.timestamp)} · {a.user_name}{a.module ? ` · ${a.module}` : ''}</p>
                      {a.note && <p className="text-[12px] text-foreground/65 mt-1 bg-muted/40 border border-border/70 rounded px-2.5 py-1.5">{a.note}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'documents' && (
              <div className="space-y-2">
                <input ref={docFileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => { uploadDocFile(Array.from(e.target.files)); e.target.value = ''; }} />
                {(d.documents || []).map((doc) => (
                  <div key={doc.key} className="rounded-lg border border-border/70 px-3.5 py-2.5 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium">{doc.label}</p>
                      <div className="flex gap-1 items-center">
                        {canEdit && <button data-testid={`doc-attach-${doc.key}`} onClick={() => attachDoc(doc.key)} title="Anexar arquivo (PDF/foto)" className="text-[11px] px-2.5 py-1.5 rounded-md font-semibold bg-primary/15 text-primary hover:bg-primary/30 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Anexar</button>}
                        {[['complete', '✓ Complete'], ['missing', '⚠ Missing'], ['not_required', 'N/A']].map(([s, l]) => (
                          <button key={s} data-testid={`doc-${doc.key}-${s}`} disabled={!canEdit} onClick={() => setDoc(doc.key, s)} className={`text-[11px] px-2.5 py-1.5 rounded-md font-semibold transition-colors disabled:opacity-40 ${doc.status === s ? (s === 'complete' ? 'bg-emerald-500/20 text-emerald-400' : s === 'missing' ? 'bg-amber-500/20 text-amber-400' : 'bg-muted/60 text-foreground/65') : 'text-muted-foreground/70 hover:text-foreground bg-muted/40'}`}>{l}</button>
                        ))}
                      </div>
                    </div>
                    {(doc.files || []).length > 0 && (
                      <div className="space-y-1">
                        {doc.files.map((f) => (
                          <div key={f.id} className="flex items-center gap-2 text-[12px] bg-muted/40 border border-border/70 rounded px-2.5 py-1.5" data-testid={`doc-file-${f.id}`}>
                            <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
                            <a href={`${API}/deals/files/${f.path}`} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate flex-1">{f.original_filename || 'arquivo'}</a>
                            <span className="text-muted-foreground/60 text-[10px] whitespace-nowrap">{f.uploaded_by}</span>
                            {canEdit && <button data-testid={`doc-file-del-${f.id}`} onClick={() => delDocFile(doc.key, f.id)} className="text-red-400/70 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {canEdit && (
                  <div className="flex gap-2">
                    <input className={inputCls} placeholder="Adicionar documento personalizado..." value={form.custom_doc || ''} onChange={(e) => setForm({ ...form, custom_doc: e.target.value })} />
                    <button data-testid="add-custom-doc" onClick={() => { if (form.custom_doc?.trim()) save('documents', { documents: [...d.documents, { key: `c_${Date.now()}`, label: form.custom_doc.trim(), status: 'missing' }] }); }} className="px-4 rounded-lg text-sm font-semibold bg-muted/50 border border-border hover:bg-muted/80 whitespace-nowrap">Adicionar</button>
                  </div>
                )}
              </div>
            )}

            {tab === 'insurance' && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Status"><select data-testid="ins-status" disabled={!canEdit} className={inputCls} value={form.ins_status ?? d.insurance?.status} onChange={(e) => setForm({ ...form, ins_status: e.target.value })}>{['Missing', 'Requested', 'Received', 'Verified', 'Not Required'].map((s) => <option key={s} className="bg-popover">{s}</option>)}</select></Field>
                <Field label="Companhia"><input disabled={!canEdit} className={inputCls} value={form.ins_company ?? d.insurance?.company} onChange={(e) => setForm({ ...form, ins_company: e.target.value })} /></Field>
                <Field label="Policy #"><input disabled={!canEdit} className={inputCls} value={form.ins_policy ?? d.insurance?.policy_number} onChange={(e) => setForm({ ...form, ins_policy: e.target.value })} /></Field>
                <Field label="Effective Date"><input disabled={!canEdit} type="date" className={inputCls} value={form.ins_eff ?? d.insurance?.effective_date} onChange={(e) => setForm({ ...form, ins_eff: e.target.value })} /></Field>
                <div className="col-span-2"><Field label="Notes"><input disabled={!canEdit} className={inputCls} value={form.ins_notes ?? d.insurance?.notes} onChange={(e) => setForm({ ...form, ins_notes: e.target.value })} /></Field></div>
                {d.insurance?.verified_by && <p className="col-span-2 text-[11px] text-muted-foreground">Verified by {d.insurance.verified_by} em {fmtDT(d.insurance.verified_at)}</p>}
                {canEdit && <button data-testid="ins-save" onClick={() => save('insurance', { status: form.ins_status ?? d.insurance?.status, company: form.ins_company ?? d.insurance?.company, policy_number: form.ins_policy ?? d.insurance?.policy_number, effective_date: form.ins_eff ?? d.insurance?.effective_date, notes: form.ins_notes ?? d.insurance?.notes }, 'Insurance atualizado')} className="col-span-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 w-fit">Salvar Insurance</button>}
              </div>
            )}

            {tab === 'warranty' && (() => {
              const w = d.warranty || {};
              const wOk = w.sold === 'no' || (w.sold === 'yes' && w.issued && w.paid);
              return (
                <div className="space-y-4" data-testid="warranty-panel">
                  {!wOk && (
                    <div className="rounded-lg border border-fuchsia-400/25 bg-fuchsia-400/[0.05] px-4 py-3 text-[12px] text-fuchsia-300 font-medium" data-testid="warranty-lock-banner">
                      Pendente: defina a garantia como <b>Emitida e Paga</b> ou <b>Não vendida</b>. Isso <b>não bloqueia</b> o avanço do deal até o Funding.
                    </div>
                  )}
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="text-sm font-semibold">Garantia estendida vendida?</p>
                    <button data-testid="war-sold-yes" disabled={!canEdit} onClick={() => save('warranty', { sold: 'yes' }, 'Garantia marcada como vendida')} className={`text-xs px-3 py-1.5 rounded-md font-semibold disabled:opacity-40 ${w.sold === 'yes' ? 'bg-fuchsia-500/20 text-fuchsia-300' : 'bg-muted/50 text-muted-foreground hover:text-foreground'}`}>Sim, vendida</button>
                    <button data-testid="war-sold-no" disabled={!canEdit} onClick={() => save('warranty', { sold: 'no' }, 'Garantia marcada como não vendida')} className={`text-xs px-3 py-1.5 rounded-md font-semibold disabled:opacity-40 ${w.sold === 'no' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-muted/50 text-muted-foreground hover:text-foreground'}`}>Não vendida</button>
                    <span className="ml-auto">{warBadge(w)}</span>
                  </div>
                  {w.sold === 'yes' && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Companhia da garantia"><input data-testid="war-company" disabled={!canEdit} className={inputCls} value={form.war_company ?? (w.company || '')} onChange={(e) => setForm({ ...form, war_company: e.target.value })} /></Field>
                        <Field label="Prazo (meses)"><input data-testid="war-term" disabled={!canEdit} type="number" className={inputCls} value={form.war_term ?? (w.term_months || '')} onChange={(e) => setForm({ ...form, war_term: e.target.value })} /></Field>
                        <Field label="Valor de compra (custo) $"><input data-testid="war-cost" disabled={!canEdit} type="number" className={inputCls} value={form.war_cost ?? (w.cost || '')} onChange={(e) => setForm({ ...form, war_cost: e.target.value })} /></Field>
                        <Field label="Valor de venda $"><input data-testid="war-price" disabled={!canEdit} type="number" className={inputCls} value={form.war_price ?? (w.price || '')} onChange={(e) => setForm({ ...form, war_price: e.target.value })} /></Field>
                        <div className="col-span-2"><Field label="Notes"><input disabled={!canEdit} className={inputCls} value={form.war_notes ?? (w.notes || '')} onChange={(e) => setForm({ ...form, war_notes: e.target.value })} /></Field></div>
                        {canEdit && <button data-testid="war-save" onClick={() => save('warranty', { company: form.war_company ?? w.company, term_months: form.war_term ?? w.term_months, cost: Number(form.war_cost ?? w.cost) || 0, price: Number(form.war_price ?? w.price) || 0, notes: form.war_notes ?? w.notes }, 'Garantia atualizada')} className="col-span-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 w-fit">Salvar Garantia</button>}
                      </div>
                      <div className="flex items-center gap-2 rounded-lg border border-border/70 p-3.5 flex-wrap">
                        <p className="text-sm font-semibold flex-1">Status da apólice</p>
                        <button data-testid="war-issued" disabled={!canEdit} onClick={() => save('warranty', { issued: !w.issued }, w.issued ? 'Emissão desmarcada' : 'Garantia emitida')} className={`text-xs px-3 py-1.5 rounded-md font-semibold disabled:opacity-40 ${w.issued ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/15 text-amber-400 hover:bg-amber-500/25'}`}>{w.issued ? '✓ Emitida' : 'Marcar Emitida'}</button>
                        <button data-testid="war-paid" disabled={!canEdit} onClick={() => save('warranty', { paid: !w.paid }, w.paid ? 'Pagamento desmarcado' : 'Garantia paga')} className={`text-xs px-3 py-1.5 rounded-md font-semibold disabled:opacity-40 ${w.paid ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/15 text-amber-400 hover:bg-amber-500/25'}`}>{w.paid ? '✓ Paga' : 'Marcar Paga'}</button>
                      </div>
                      {(w.issued_by || w.paid_by) && <p className="text-[11px] text-muted-foreground">{w.issued_by ? `Emitida por ${w.issued_by} em ${fmtDT(w.issued_at)}` : ''}{w.issued_by && w.paid_by ? ' · ' : ''}{w.paid_by ? `Paga confirmada por ${w.paid_by} em ${fmtDT(w.paid_at)}` : ''}</p>}
                      <div className="rounded-lg border border-border/70 p-3.5 space-y-2">
                        <input ref={warFileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => { uploadWarrantyFile(Array.from(e.target.files)); e.target.value = ''; }} />
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] uppercase font-bold text-muted-foreground/70">Anexos (contrato / comprovante — PDF ou foto)</p>
                          {canEdit && <button data-testid="war-attach" onClick={() => warFileRef.current?.click()} className="text-[11px] px-2.5 py-1.5 rounded-md font-semibold bg-primary/15 text-primary hover:bg-primary/30 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Anexar</button>}
                        </div>
                        {(w.attachments || []).map((f) => (
                          <div key={f.id} className="flex items-center gap-2 text-[12px] bg-muted/40 border border-border/70 rounded px-2.5 py-1.5" data-testid={`war-file-${f.id}`}>
                            <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
                            <a href={`${API}/deals/files/${f.path}`} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate flex-1">{f.original_filename || 'arquivo'}</a>
                            <span className="text-muted-foreground/60 text-[10px] whitespace-nowrap">{f.uploaded_by}</span>
                            {canEdit && <button data-testid={`war-file-del-${f.id}`} onClick={() => delWarrantyFile(f.id)} className="text-red-400/70 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>}
                          </div>
                        ))}
                        {(w.attachments || []).length === 0 && <p className="text-[12px] text-muted-foreground/60">Nenhum anexo ainda.</p>}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            {tab === 'title' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <p className="text-sm font-semibold">Is Title In House?</p>
                  <button data-testid="title-inhouse-yes" disabled={!canEdit} onClick={() => save('title', { in_house: true }, 'Title In House')} className={`text-xs px-3 py-1.5 rounded-md font-semibold ${d.title?.in_house ? 'bg-emerald-500/20 text-emerald-400' : 'bg-muted/50 text-muted-foreground hover:text-foreground'}`}>Yes</button>
                  <button data-testid="title-inhouse-no" disabled={!canEdit} onClick={() => save('title', { in_house: false, status: 'Not In House' })} className={`text-xs px-3 py-1.5 rounded-md font-semibold ${!d.title?.in_house ? 'bg-amber-500/20 text-amber-400' : 'bg-muted/50 text-muted-foreground hover:text-foreground'}`}>No</button>
                  <span className="ml-auto">{subBadge(d.title?.status)}</span>
                </div>
                {d.title?.in_house && <p className="text-[11px] text-muted-foreground">Verified by {d.title.verified_by} · {d.title.verified_date}</p>}
                {!d.title?.in_house && d.title?.status !== 'Received' && (
                  <div className="rounded-lg border border-amber-400/20 bg-amber-400/[0.04] p-4 space-y-3">
                    <p className="text-xs font-bold text-amber-400 uppercase tracking-wide">Request Title from Floor</p>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Floor Company"><input data-testid="floor-company" disabled={!canEdit} className={inputCls} value={form.floor_company || ''} onChange={(e) => setForm({ ...form, floor_company: e.target.value })} /></Field>
                      <Field label="Expected Date"><input type="date" disabled={!canEdit} className={inputCls} value={form.expected_date || ''} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} /></Field>
                      <Field label="Reference #"><input disabled={!canEdit} className={inputCls} value={form.reference || ''} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></Field>
                      <Field label="Notes"><input disabled={!canEdit} className={inputCls} value={form.title_notes || ''} onChange={(e) => setForm({ ...form, title_notes: e.target.value })} /></Field>
                    </div>
                    {canEdit && <button data-testid="request-floor-btn" onClick={requestFloor} className="px-4 py-2.5 rounded-lg text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-black">REQUEST TITLE FROM FLOOR</button>}
                  </div>
                )}
                {d.title?.status === 'Requested from Floor' && (
                  <div className="text-[12px] text-muted-foreground space-y-1">
                    <p>Solicitado a <b>{d.title.floor_company || '—'}</b> em {d.title.request_date} por {d.title.requested_by} {d.title.expected_date ? `· previsto ${d.title.expected_date}` : ''}</p>
                    {canEdit && <button data-testid="title-received-btn" onClick={() => save('title', { status: 'Received' }, 'Title Received')} className="mt-2 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white">Marcar Title Received</button>}
                  </div>
                )}
              </div>
            )}

            {tab === 'registration' && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Status"><select data-testid="reg-status" disabled={!canEdit} className={inputCls} value={form.reg_status ?? d.registration?.status} onChange={(e) => setForm({ ...form, reg_status: e.target.value })}>{['Pending', 'Submitted', 'Completed'].map((s) => <option key={s} className="bg-popover">{s}</option>)}</select></Field>
                <Field label="Location / Runner"><input disabled={!canEdit} className={inputCls} value={form.reg_loc ?? d.registration?.location} onChange={(e) => setForm({ ...form, reg_loc: e.target.value })} /></Field>
                <Field label="Registration #"><input disabled={!canEdit} className={inputCls} value={form.reg_num ?? d.registration?.registration_number} onChange={(e) => setForm({ ...form, reg_num: e.target.value })} /></Field>
                <Field label="Plate #"><input disabled={!canEdit} className={inputCls} value={form.reg_plate ?? d.registration?.plate_number} onChange={(e) => setForm({ ...form, reg_plate: e.target.value })} /></Field>
                <div className="col-span-2"><Field label="Notes"><input disabled={!canEdit} className={inputCls} value={form.reg_notes ?? d.registration?.notes} onChange={(e) => setForm({ ...form, reg_notes: e.target.value })} /></Field></div>
                {d.registration?.submission_date && <p className="col-span-2 text-[11px] text-muted-foreground">Submitted {d.registration.submission_date} por {d.registration.submitted_by} {d.registration.completion_date ? `· Completed ${d.registration.completion_date}` : ''}</p>}
                {canEdit && <button data-testid="reg-save" onClick={() => save('registration', { status: form.reg_status ?? d.registration?.status, location: form.reg_loc ?? d.registration?.location, registration_number: form.reg_num ?? d.registration?.registration_number, plate_number: form.reg_plate ?? d.registration?.plate_number, notes: form.reg_notes ?? d.registration?.notes }, 'Registration atualizado')} className="col-span-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 w-fit">Salvar Registration</button>}
              </div>
            )}

            {tab === 'service' && (
              <div className="space-y-4">
                <DeliveryPanel d={d} onChanged={() => { load(); onChanged && onChanged(); }} canDeliver={canDeliver || d.salesperson_id === user?.user_id} />
                <ServiceInspectionPanel d={d} onChanged={() => { load(); onChanged && onChanged(); }} canEdit={canInspect || d.salesperson_id === user?.user_id} />
                <div className="rounded-lg border border-border/70 p-3" data-testid="deal-inspection-report">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Laudo da inspeção de entrada (Service)</p>
                  <InspectionReport vehicleId={d.vehicle_id} />
                </div>
              </div>
            )}

            {tab === 'notes' && (
              <div className="space-y-3">
                <div className="space-y-2.5 max-h-[280px] overflow-y-auto">
                  {(data.notes || []).map((n) => (
                    <div key={n.id} className="rounded-lg border border-border/70 p-3">
                      <p className="text-[11px] font-bold text-primary">{n.user_name} — {n.user_role} <span className="text-muted-foreground/60 font-normal">· {fmtDT(n.timestamp)} · {n.module}</span></p>
                      <p className="text-sm mt-1">{n.note}</p>
                    </div>
                  ))}
                  {(data.notes || []).length === 0 && <p className="text-sm text-muted-foreground/60 text-center py-6">Nenhuma note ainda.</p>}
                </div>
                <div className="flex gap-2">
                  <input data-testid="deal-note-input" className={inputCls} placeholder="Escrever note..." value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addNote()} />
                  <button data-testid="deal-note-save" onClick={addNote} className="px-4 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 whitespace-nowrap"><StickyNote className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const FiDeals = () => {
  const { user } = useAuth();
  const isFinView = ['Administrator', 'Owner'].includes(user?.role) || (user?.role || '').endsWith('Manager');
  const canEditDeals = ['Owner', 'Administrator', 'Finance Manager'].includes(user?.role);
  const [manualOpen, setManualOpen] = useState(false);
  const [view, setView] = useState('board');
  const [deals, setDeals] = useState([]);
  const [kpis, setKpis] = useState({});
  const [sales, setSales] = useState([]);
  const [q, setQ] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [arcRange, setArcRange] = useState({ from: '', to: '' });
  useDeepLink('deal', setDetailId);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (view === 'attention') params.set('view', 'attention');
      if (view === 'archived') {
        params.set('archived', 'true');
        if (arcRange.from) params.set('date_from', arcRange.from);
        if (arcRange.to) params.set('date_to', arcRange.to);
      }
      const [dl, kp] = await Promise.all([api.get(`/deals?${params.toString()}`), api.get('/deals/kpis')]);
      setDeals(dl.data); setKpis(kp.data);
    } catch (e) {}
  }, [q, view, arcRange]);
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, [load]);
  useEffect(() => { if (view === 'sales') api.get('/sales').then((r) => setSales(r.data)).catch(() => {}); }, [view]);

  return (
    <div className="space-y-5" data-testid="fi-deals-page">
      <div className="ent-ribbon" data-testid="fi-kpis">
        {[['Active Deals', kpis.active, ''], ['Wait Warranty', kpis.waiting_warranty, 'warn'], ['Wait Insurance', kpis.waiting_insurance, 'warn'],
          ['Wait Title', kpis.waiting_title, 'warn'], ['Wait Registration', kpis.waiting_registration, 'warn'],
          ['Reg. Today', kpis.registration_completed_today, ''], ['Docs Missing', kpis.docs_missing, 'bad'],
          ['In Service', kpis.ready_for_service, ''], ['Ready Delivery', kpis.ready_for_delivery, ''],
          ['Delivered Today', kpis.delivered_today, ''], ['Overdue', kpis.overdue, 'bad']].map(([l, val, sev]) => (
          <div key={l}>
            <p className="ent-label whitespace-nowrap">{l}</p>
            <p className={`ent-value mt-1 ${val > 0 && sev === 'warn' ? 't-warn' : val > 0 && sev === 'bad' ? 't-bad' : ''}`}>{val ?? 0}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="ent-tabs">
          {[['board', LayoutGrid, 'Board'], ['list', List, 'List'], ['attention', AlertTriangle, 'Attention'], ['history', FolderSearch, 'Negociações'], ['sales', ReceiptText, 'Histórico de Vendas'], ['archived', Archive, 'Arquivados']].map(([k, Icon, l]) => (
            <button key={k} data-testid={`fi-view-${k}`} onClick={() => setView(k)} className={`ent-tab flex items-center gap-1.5 ${view === k ? 'ent-tab-active' : ''}`}><Icon className="w-3.5 h-3.5" /> {l}</button>
          ))}
        </div>
        {view !== 'history' && (
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
            <input data-testid="fi-search" className="ent-input pl-8 w-[280px]" placeholder="Cliente, VIN, ano/marca/modelo, stock, deal..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        )}
        {!canEditDeals && (
          <p data-testid="fi-readonly-banner" className="text-[11.5px] t-warn border-l-2 border-[hsl(var(--warn))] pl-2.5">
            Você pode abrir qualquer deal e adicionar notes — a edição é restrita ao F&I / Admin.
          </p>
        )}
        {isFinView && (
          <button data-testid="fi-manual-deal-btn" onClick={() => setManualOpen(true)} className="ent-btn ent-btn-primary whitespace-nowrap"><Plus className="w-3.5 h-3.5" /> Deal manual</button>
        )}
      </div>

      {view === 'board' && (
        <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory md:snap-none">
          {Object.entries(DEAL_STAGES).map(([k, m]) => {
            const col = deals.filter((d) => d.status === k);
            return (
              <div key={k} className="min-w-[86vw] w-[86vw] md:min-w-[400px] md:w-[400px] shrink-0 snap-start ent-col" data-testid={`fi-col-${k}`}>
                <div className="ent-colhead" style={{ borderTop: `2px solid ${STAGE_BAR[k] || 'hsl(var(--primary))'}` }}>
                  <span className="w-[3px] h-3.5 rounded-[1px]" style={{ background: STAGE_BAR[k] || 'hsl(var(--primary))' }} />
                  <span className="ent-coltitle">{m.label}</span>
                  <span className="ent-count">({col.length})</span>
                </div>
                {col.length === 0 ? (
                  <p className="ent-empty">Nenhum deal nesta etapa</p>
                ) : (
                  <table className="ent-coltable" style={{ tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '40%' }}>Cliente / Veículo</th>
                        <th className="text-center" style={{ width: 44 }}>Idade</th>
                        <th style={{ width: '32%' }}>Próxima ação</th>
                        <th className="text-center" style={{ width: 80 }}>F&amp;I</th>
                      </tr>
                    </thead>
                    <tbody>
                      {col.map((d) => <DealRow key={d.id} d={d} onOpen={setDetailId} />)}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(view === 'list' || view === 'attention') && (
        <div className="rounded-xl border border-border/70 bg-card p-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground text-xs tracking-wider border-b border-border/70">
              <th className="pb-3 font-semibold">DEAL</th><th className="pb-3 font-semibold">VEHICLE</th>
              <th className="pb-3 font-semibold hidden md:table-cell">SALESPERSON</th><th className="pb-3 font-semibold">STAGE</th>
              <th className="pb-3 font-semibold hidden lg:table-cell">INS / WAR / TITLE / REG</th>
              <th className="pb-3 font-semibold">AGE</th><th className="pb-3 font-semibold">NEXT ACTION</th>
            </tr></thead>
            <tbody>
              {deals.map((d) => {
                const m = DEAL_STAGES[d.status] || {};
                return (
                  <tr key={d.id} data-testid={`fi-row-${d.id}`} onClick={() => setDetailId(d.id)} className="border-b border-border/60 row-hover cursor-pointer">
                    <td className="py-3"><p className="font-medium">{d.customer_name}{d.canceled && <span className="ent-tag t-bad ml-1.5">CANCELADA</span>}</p><p className="text-[11px] text-muted-foreground/70">{d.id}</p></td>
                    <td className="py-3 text-foreground/75">{d.vehicle_desc}<p className="text-[11px] text-muted-foreground/70">#{d.stock}</p></td>
                    <td className="py-3 text-foreground/65 hidden md:table-cell">{d.salesperson_name}</td>
                    <td className="py-3"><span className={`text-[11px] px-2 py-1 rounded-full font-semibold whitespace-nowrap ${m.cls}`}>{m.label}</span></td>
                    <td className="py-3 hidden lg:table-cell"><div className="flex gap-2">{subBadge(d.insurance?.status)}{warBadge(d.warranty)}{subBadge(d.title?.status)}{subBadge(d.registration?.status)}</div></td>
                    <td className="py-3"><AgeBadge days={d.deal_age} /></td>
                    <td className="py-3 text-[11px] font-bold text-primary uppercase whitespace-nowrap">→ {d.next_action}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {deals.length === 0 && <p className="text-center py-12 text-muted-foreground/70">{view === 'attention' ? 'Nenhum deal precisando de ação. 👌' : 'Nenhum deal ainda. Marque um lead como Contract Signed no CRM.'}</p>}
        </div>
      )}

      {view === 'history' && <DealHistory onOpen={setDetailId} stages={DEAL_STAGES} />}

      {view === 'sales' && (
        <div className="space-y-4" data-testid="fi-sales-history">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[['Faturamento total', formatCurrency(sales.filter((s) => s.status === 'Concluída').reduce((a, s) => a + s.valor, 0))],
              ['Lucro total', formatCurrency(sales.filter((s) => s.status === 'Concluída').reduce((a, s) => a + s.lucro, 0))],
              ['Vendas concluídas', sales.filter((s) => s.status === 'Concluída').length]].map(([l, v]) => (
              <div key={l} className="rounded-xl border border-border bg-card px-4 py-3 glow-border card-hover">
                <p className="text-[10px] tracking-widest text-muted-foreground/70 font-bold uppercase">{l}</p>
                <p className="font-display font-bold text-xl text-foreground">{v}</p>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-border/70 bg-card p-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground text-xs tracking-wider border-b border-border/70">
                <th className="pb-3 font-semibold">VEÍCULO</th><th className="pb-3 font-semibold">CLIENTE</th>
                <th className="pb-3 font-semibold hidden md:table-cell">VENDEDOR</th><th className="pb-3 font-semibold hidden lg:table-cell">PAGAMENTO</th>
                <th className="pb-3 font-semibold hidden sm:table-cell">DATA</th><th className="pb-3 font-semibold text-right">VALOR</th>
                <th className="pb-3 font-semibold text-right hidden md:table-cell">LUCRO</th><th className="pb-3 font-semibold text-right">DEAL</th>
              </tr></thead>
              <tbody>
                {sales.map((s) => (
                  <tr key={s.id} className="border-b border-border/60 row-hover" data-testid={`sale-row-${s.id}`}>
                    <td className="py-3.5 font-medium">{s.veiculo}</td>
                    <td className="py-3.5 text-foreground/75">{s.cliente}</td>
                    <td className="py-3.5 text-foreground/75 hidden md:table-cell">{s.vendedor}</td>
                    <td className="py-3.5 hidden lg:table-cell"><span className="text-[11px] px-2 py-1 rounded text-foreground/65 bg-muted/50">{s.formaPagamento}</span></td>
                    <td className="py-3.5 text-muted-foreground hidden sm:table-cell">{formatDate(s.data)}</td>
                    <td className="py-3.5 text-right font-semibold">{formatCurrency(s.valor)}</td>
                    <td className="py-3.5 text-right text-emerald-400 font-semibold hidden md:table-cell">{formatCurrency(s.lucro)}</td>
                    <td className="py-3.5 text-right">{s.deal_id ? <button onClick={() => setDetailId(s.deal_id)} className="text-[11px] font-bold text-primary hover:underline" data-testid={`sale-open-deal-${s.deal_id}`}>{s.deal_id}</button> : <span className="text-muted-foreground/50 text-[11px]">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sales.length === 0 && <p className="text-center py-12 text-muted-foreground/70">Nenhuma venda registrada ainda.</p>}
          </div>
        </div>
      )}

      {view === 'archived' && (
        <div className="space-y-3" data-testid="fi-archived">
          <div className="flex flex-wrap items-end gap-2 border-y border-border py-3">
            <div><p className="ent-label mb-1">Entregue de</p><input data-testid="arc-from" type="date" className="ent-input" value={arcRange.from} onChange={(e) => setArcRange({ ...arcRange, from: e.target.value })} /></div>
            <div><p className="ent-label mb-1">até</p><input data-testid="arc-to" type="date" className="ent-input" value={arcRange.to} onChange={(e) => setArcRange({ ...arcRange, to: e.target.value })} /></div>
            {(arcRange.from || arcRange.to) && <button data-testid="arc-clear" onClick={() => setArcRange({ from: '', to: '' })} className="ent-btn h-8 px-3">Limpar datas</button>}
            <p className="ml-auto text-[11.5px] text-muted-foreground">Use a busca acima por cliente, veículo, stock, VIN ou deal · <span className="num font-semibold text-foreground">{deals.length}</span> arquivados</p>
          </div>
          <div className="overflow-x-auto">
            <table className="cm-table" data-testid="arc-table">
              <thead><tr>
                <th style={{ width: 86 }}>Entrega</th><th style={{ width: 80 }}>Deal</th><th>Veículo</th><th>Cliente</th>
                <th>Vendedor</th><th className="cm-num" style={{ width: 110 }}>Venda</th><th style={{ width: 120 }}>Arquivado</th><th style={{ width: 108 }} />
              </tr></thead>
              <tbody>
                {deals.map((d) => (
                  <tr key={d.id} data-testid={`arc-row-${d.id}`} onClick={() => setDetailId(d.id)}>
                    <td className="num cm-muted">{(d.delivery?.date || d.sale_date || '').slice(5) || '—'}</td>
                    <td className="num">{d.id}</td>
                    <td className="max-w-[240px] truncate font-medium">{d.vehicle_desc}<span className="cm-muted"> · #{d.stock}</span></td>
                    <td className="max-w-[170px] truncate">{d.customer_name}</td>
                    <td className="max-w-[150px] truncate">{d.salesperson_name}</td>
                    <td className="cm-money">{formatCurrency(d.sale_price || 0)}</td>
                    <td className="cm-muted text-[11px]">{d.archived_at ? `${new Date(d.archived_at).toLocaleDateString('pt-BR')} · ${d.archived_by || ''}` : '—'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {isFinView && (
                        <button data-testid={`arc-unarchive-${d.id}`} className="ent-btn ent-btn-sm"
                          onClick={async () => { try { await api.post(`/deals/${d.id}/unarchive`); toast({ title: 'Deal desarquivado' }); load(); } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); } }}>
                          <ArchiveRestore className="w-3 h-3" /> Desarquivar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!deals.length && <p className="ent-empty">Nenhum deal arquivado nesse filtro. Arquive um deal entregue pelo botão dentro do deal.</p>}
          </div>
        </div>
      )}

      <DealDetailDialog dealId={detailId} open={!!detailId} onClose={() => setDetailId(null)} onChanged={load} />
      <ManualDealDialog open={manualOpen} onClose={() => setManualOpen(false)} onCreated={load} />
    </div>
  );
};

export default FiDeals;
